/**
 * useUserPush — ONE user-scoped SignalR subscription (`user-<oid>`) for a
 * mounted pane, so a queue about runs the AM has never opened can stay live.
 *
 * Shared by ora and the Outlook add-in (`@product-app/common/workflows/…`).
 * Built on the shared `useSignalR` with the module-level user sentinel as its
 * key, NOT on `apps/outlook-addin/src/hooks/useActiveInstances.ts` — that hook
 * is the add-in's active-tab list and subscribes PER ROW, which is exactly the
 * shape this channel exists to avoid.
 *
 * WHAT IT DOES NOT DO — design Constraint 19:
 * events carry no payload the caller may accumulate. A subscriber is told
 * "something changed" and answers with a refetch. A count or a list assembled
 * from push frames is forbidden: the server's read is the only truth, and a
 * client-side accumulator is a second one that drifts silently.
 *
 * ON "EXACTLY ONE CONNECTION" (design Constraint 18's CHECK), stated here so
 * an acceptance auditor does not read it literally against correct code:
 * it means exactly one USER-scoped connection, which is what the constraint is
 * about. It does NOT mean the pane holds one socket in total. `useSignalR`
 * keys its connection map by id and `useActiveInstances` already subscribes
 * per row, so a pinned pane with a populated `active` tab holds 1 user
 * connection + N instance connections. Those N are PRE-EXISTING and untouched
 * by this story.
 */

import { useCallback, useEffect, useRef } from "react";
import { useSignalR } from "./useSignalR";
import { USER_CHANNEL_KEY, type GetUserPushChannelFn } from "./signalrClient";

export interface UseUserPushOptions {
  /** Resolve the user-scoped negotiate descriptor. Takes no instance id. */
  getUserPushChannel: GetUserPushChannelFn;
  /** Whether to actively subscribe. Default true. */
  enabled?: boolean;
  /** Disable side effects in tests. */
  disabled?: boolean;
}

export interface UseUserPushResult {
  /** True while the single user-scoped WebSocket is open. */
  isConnected: boolean;
  /**
   * True once negotiate failed cleanly (503) or connect failed repeatedly.
   * Sticky — the connect effect then skips entirely rather than retrying
   * tightly, and the consumer's poll becomes the only feed.
   */
  isSignalRUnavailable: boolean;
  /** Subscribe to a hub event on the user connection. Returns an unsubscribe. */
  subscribe: <P = unknown>(event: string, handler: (payload: P) => void) => () => void;
}

export function useUserPush(options: UseUserPushOptions): UseUserPushResult {
  const { getUserPushChannel, enabled = true, disabled = false } = options;

  // Hold the resolver in a ref and expose a STABLE adapter. Without this an
  // unmemoized arrow from the caller changes identity every render →
  // useSignalR's `connect` is regenerated → the connect effect tears down and
  // re-opens the socket on every render. Mirrors useInstancePush's treatment
  // of getStatus / joinSignalRGroup.
  const getUserPushChannelRef = useRef(getUserPushChannel);
  getUserPushChannelRef.current = getUserPushChannel;
  const resolveChannel = useCallback(
    () => getUserPushChannelRef.current(),
    [],
  );

  const signalR = useSignalR(resolveChannel);

  useEffect(() => {
    if (!enabled || disabled) return;
    if (signalR.isSignalRUnavailable) {
      console.info(
        "[useUserPush] SignalR skip — marked unavailable; the queue's poll is the only feed",
      );
      return;
    }

    let cancelled = false;
    void (async () => {
      await signalR.connect(USER_CHANNEL_KEY);
      // The effect was cleaned up while connect was in flight — the cleanup's
      // disconnect ran before the socket existed, so close it now.
      if (cancelled) void signalR.disconnect(USER_CHANNEL_KEY);
    })();

    return () => {
      cancelled = true;
      void signalR.disconnect(USER_CHANNEL_KEY);
    };
    // signalR.connect / disconnect are stable useCallbacks over a stable
    // resolver, so they are deliberately not in the dep array — including
    // them would not change when this runs, only how noisy the lint is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, disabled, signalR.isSignalRUnavailable]);

  return {
    isConnected: signalR.isConnected,
    isSignalRUnavailable: signalR.isSignalRUnavailable,
    subscribe: signalR.subscribe,
  };
}

export default useUserPush;
